/**
 * Exam Generator — builds exam from question pool with domain-weighted distribution.
 */

import { DOMAINS, EXAM_QUESTION_COUNT } from '../../../shared/constants/domains';
import { generateUUID } from '../../../shared/utils/uuid';

/**
 * Generate a full practice exam with domain-weighted question distribution.
 *
 * @param {Array} pool - Full question pool
 * @param {Object} config - { type: 'full'|'quick'|'weak_spots', domain, questionCount, timeLimitMinutes }
 * @param {Array} pastExams - Previous exam records (for weak spots)
 * @returns {Object} { id, type, questions, time_limit_minutes }
 */
export function generateExam(pool, config = {}) {
  const {
    type = 'full',
    domain = null,
    questionCount = EXAM_QUESTION_COUNT,
    timeLimitMinutes = 120,
  } = config;

  let selected = [];

  if (type === 'quick' && domain) {
    // Quick quiz: from specific domain
    const domainPool = pool.filter(q => q.domain === domain);
    selected = shuffleAndTake(domainPool, questionCount);
  } else if (type === 'full') {
    // Full exam: domain-weighted distribution
    selected = weightedSelection(pool, questionCount);
  } else {
    // Default: random
    selected = shuffleAndTake(pool, questionCount);
  }

  return {
    id: generateUUID(),
    type,
    questions: selected.map(q => q.id),
    questionObjects: selected,
    time_limit_minutes: timeLimitMinutes,
    started_at: new Date().toISOString(),
  };
}

/**
 * Generate a weak spots exam from historically wrong questions.
 */
export function generateWeakExam(pool, pastExams, questionCount = 30) {
  // Build map of question performance
  const perfMap = {};
  for (const exam of pastExams) {
    for (const qId of exam.questions) {
      if (!perfMap[qId]) perfMap[qId] = { attempts: 0, correct: 0 };
      perfMap[qId].attempts++;
      const answer = exam.answers?.[qId];
      const question = pool.find(q => q.id === qId);
      if (question && isAnswerCorrect(question, answer)) {
        perfMap[qId].correct++;
      }
    }
  }

  // Score each question: lower = weaker
  const scored = pool.map(q => {
    const perf = perfMap[q.id];
    if (!perf || perf.attempts === 0) return { question: q, score: 0.5 }; // Unseen = medium priority
    return { question: q, score: perf.correct / perf.attempts };
  });

  // Sort weakest first, take requested count
  scored.sort((a, b) => a.score - b.score);
  const selected = scored.slice(0, questionCount).map(s => s.question);

  return {
    id: generateUUID(),
    type: 'weak_spots',
    questions: selected.map(q => q.id),
    questionObjects: selected,
    time_limit_minutes: Math.ceil(questionCount * 1.2), // ~1.2 min per question
    started_at: new Date().toISOString(),
  };
}

/**
 * Select questions weighted by domain exam percentages.
 */
function weightedSelection(pool, totalCount) {
  const selected = [];
  const usedIds = new Set();

  for (const domain of DOMAINS) {
    const target = Math.round(totalCount * domain.weight);
    const domainPool = pool.filter(q => q.domain === domain.id && !usedIds.has(q.id));
    const picked = shuffleAndTake(domainPool, target);
    picked.forEach(q => usedIds.add(q.id));
    selected.push(...picked);
  }

  // If we're short due to rounding, fill from remaining pool
  while (selected.length < totalCount) {
    const remaining = pool.filter(q => !usedIds.has(q.id));
    if (remaining.length === 0) break;
    const pick = remaining[Math.floor(Math.random() * remaining.length)];
    usedIds.add(pick.id);
    selected.push(pick);
  }

  // Shuffle final order
  return shuffleArray(selected);
}

function shuffleAndTake(arr, count) {
  return shuffleArray(arr).slice(0, count);
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function isAnswerCorrect(question, answer) {
  if (!answer) return false;
  if (Array.isArray(question.correct_answer)) {
    if (!Array.isArray(answer)) return false;
    return question.correct_answer.length === answer.length &&
      question.correct_answer.every(a => answer.includes(a));
  }
  return answer === question.correct_answer;
}
